import { useCallback, useEffect, useMemo, useRef } from 'react'
import { log } from '@livekit/components-core'
import { ConnectionState, ParticipantEvent, RoomEvent, Track } from 'livekit-client'
import type { LocalTrackPublication, Room } from 'livekit-client'

import { canPublishMicrophone, isRoomMicrophoneBlocked } from '@/src/lib/microphonePermissions'

interface MicrophoneBlockOptions {
  enforceRoomMicrophoneBan?: boolean
  token?: string
  serverUrl?: string
}

/** Apply the room default after joining, then honor individual participant permissions. */
export function useLocalMicrophonePermissions(room?: Room | null, options: MicrophoneBlockOptions = {}) {
  const latest = useRef(options)
  latest.current = options
  // A refreshed JWT must not reset the already applied entry policy.
  const policy = useMemo(() => ({
    room, initialized: false, blocked: false, pending: false, syncing: false,
  }), [room])

  const canEnableMicrophone = useCallback(() => {
    if (!policy.room) return false
    const permissions = policy.room.localParticipant.permissions
    const blocked = latest.current.enforceRoomMicrophoneBan === true &&
      !permissions?.agent && !permissions?.recorder &&
      isRoomMicrophoneBlocked(policy.room.metadata)
    const allowed = canPublishMicrophone(permissions)
    if (!policy.initialized || blocked !== policy.blocked) {
      policy.initialized = true
      policy.blocked = blocked
      policy.pending = blocked && allowed
    }
    if (!blocked || !allowed) policy.pending = false
    return allowed && !policy.pending
  }, [policy])

  useEffect(() => {
    if (!room) return
    const participant = room.localParticipant
    let disposed = false
    let muting = false
    let recheck = false

    const enforce = async () => {
      if (disposed) return
      if (muting) {
        recheck = true
        return
      }
      const allowed = policy.initialized
        ? canEnableMicrophone() : canPublishMicrophone(participant.permissions)
      if (allowed || !participant.isMicrophoneEnabled) return

      muting = true
      try {
        await participant.setMicrophoneEnabled(false)
      } catch (error) {
        log.warn('Could not apply local microphone permissions', error)
      } finally {
        muting = false
        if (recheck) {
          recheck = false
          void enforce()
        }
      }
    }

    const syncOwnBlock = async () => {
      const { token, serverUrl } = latest.current
      if (disposed || !policy.pending || policy.syncing || !token || !serverUrl ||
        room.state !== ConnectionState.Connected || !participant.identity) return
      policy.syncing = true
      try {
        const response = await fetch(`/api/block_mute_participants?livekit_url=${encodeURIComponent(serverUrl)}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            room: room.name, participantIdentities: [participant.identity],
            grantAccess: false, updateRoomMetadata: false, trackType: 'microphone',
          }),
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok || result.updatedCount !== 1) {
          throw new Error(`Self microphone block was not applied: ${response.status}`)
        }
        // LiveKit's permission event confirms the restriction. An HTTP reply
        // alone must not clear the local guard or overwrite a later grant.
      } catch (error) {
        if (!disposed) log.warn('Could not synchronize own microphone block', error)
      } finally {
        policy.syncing = false
      }
    }

    const onSignalConnected = () => {
      canEnableMicrophone()
      void enforce()
    }
    const onConnected = () => {
      canEnableMicrophone()
      void enforce()
      void syncOwnBlock()
    }
    const onPermissionsChanged = () => {
      // Join response metadata can arrive before initial permissions. Only
      // SignalConnected/Connected initialize the entry policy.
      void enforce()
    }
    const onMetadataChanged = () => {
      if (!policy.initialized) return
      const wasBlocked = policy.blocked
      canEnableMicrophone()
      void enforce()
      if (!wasBlocked && policy.blocked) void syncOwnBlock()
    }
    const onMicrophoneTrack = (publication: LocalTrackPublication) => {
      if (publication.source === Track.Source.Microphone) void enforce()
    }

    room.on(RoomEvent.SignalConnected, onSignalConnected)
    room.on(RoomEvent.Connected, onConnected)
    room.on(RoomEvent.Reconnected, onConnected)
    room.on(RoomEvent.RoomMetadataChanged, onMetadataChanged)
    participant.on(ParticipantEvent.ParticipantPermissionsChanged, onPermissionsChanged)
    participant.on(ParticipantEvent.LocalTrackPublished, onMicrophoneTrack)
    participant.on(ParticipantEvent.TrackUnmuted, onMicrophoneTrack)
    if (room.state === ConnectionState.Connected) onConnected()

    return () => {
      disposed = true
      room.off(RoomEvent.SignalConnected, onSignalConnected)
      room.off(RoomEvent.Connected, onConnected)
      room.off(RoomEvent.Reconnected, onConnected)
      room.off(RoomEvent.RoomMetadataChanged, onMetadataChanged)
      participant.off(ParticipantEvent.ParticipantPermissionsChanged, onPermissionsChanged)
      participant.off(ParticipantEvent.LocalTrackPublished, onMicrophoneTrack)
      participant.off(ParticipantEvent.TrackUnmuted, onMicrophoneTrack)
    }
  }, [room, policy, canEnableMicrophone, options.enforceRoomMicrophoneBan])

  return canEnableMicrophone
}
